import React from 'react';
import { XCircle, Loader2 } from 'lucide-react';
import { Modal } from '../../ui/Modal';
import { Field, Textarea } from '../../ui/Field';
import { pillSecondary, pillDestructive, PILL_DESTRUCTIVE_STYLE } from '../../ui/pills';
import type { PipelineTransition } from '../pipelineTransitions';

/**
 * Abandon du dossier — « Marquer perdue » avant la signature, « Annuler la privatisation » après
 * (voir `getAbandonTransition`). Prend la place du « Supprimer le dossier » de la maquette 18a, en bas à
 * gauche de la fiche : décision d'Emeni (16/09), rien n'est jamais effacé, le motif alimente le bilan.
 *
 * Confirmation en 400 px qui nomme ce qui va se passer (passation §6) ; le motif est obligatoire, comme
 * le serveur l'exige. Rouge plein : c'est l'issue définitive du dossier.
 */
export function AbandonDossierModal({ label, transition, saving, error, onConfirm, onClose }: {
    label: string;
    transition: PipelineTransition;
    saving: boolean;
    error?: string;
    onConfirm: (motif: string) => Promise<void> | void;
    onClose: () => void;
}) {
    const [motif, setMotif] = React.useState('');
    const champ = transition.requiresFields?.[0];
    const perdue = transition.nextStage === 'perdue';

    return (
        <Modal
            open
            onClose={onClose}
            size="sm"
            title={transition.actionLabel}
            subtitle={perdue ? 'Le dossier sort du parcours commercial.' : 'La privatisation vendue est annulée.'}
            icon={<XCircle size={16} style={{ color: 'var(--color-destructive)' }} />}
            footer={
                <>
                    <button type="button" onClick={onClose} className={pillSecondary()} disabled={saving}>Annuler</button>
                    <button type="button" className={pillDestructive()} style={PILL_DESTRUCTIVE_STYLE}
                        onClick={() => onConfirm(motif.trim())} disabled={!motif.trim() || saving}>
                        {saving ? <Loader2 size={13} className="animate-spin" /> : null} {transition.actionLabel}
                    </button>
                </>
            }
        >
            <div className="space-y-3">
                <p className="text-[13px] leading-relaxed text-content-muted text-pretty">
                    Le dossier <strong className="text-content">{label}</strong> passera à « {perdue ? 'Perdue' : 'Annulée'} ».
                    {' '}{transition.hint}
                </p>
                <Field label={champ?.label || 'Motif'} required>
                    {({ id }) => <Textarea id={id} rows={3} value={motif} onChange={(e) => setMotif(e.target.value)} autoFocus />}
                </Field>
                {error && <p className="text-[12px] text-state-danger font-semibold">{error}</p>}
            </div>
        </Modal>
    );
}
